import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, Moon, Briefcase, Umbrella } from "lucide-react";


const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function CalendarPage() {
  const { user } = useAuth();
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchCalendar = (year, month) => {
    setLoading(true);
    API.get(`/calendar?year=${year}&month=${month}`)
      .then(({ data }) => setCalendarData(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCalendar(currentYear, currentMonth);
  }, [currentYear, currentMonth]);

  const daysInMonth = useMemo(() => {
    if (!calendarData?.days) return [];
    return calendarData.days;
  }, [calendarData]);

  const firstDayOfMonth = useMemo(() => {
    const d = new Date(currentYear, currentMonth - 1, 1);
    // Monday=0, Sunday=6
    let day = d.getDay() - 1;
    if (day < 0) day = 6; // Sunday
    return day;
  }, [currentYear, currentMonth]);

  const goPrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
    setSelectedDay(null);
  };

  const goNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
    setSelectedDay(null);
  };

  const goToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth() + 1);
    setSelectedDay(null);
  };

  const selectedDayInfo = useMemo(() => {
    if (!selectedDay || !daysInMonth.length) return null;
    return daysInMonth.find((d) => d.date === selectedDay) || null;
  }, [selectedDay, daysInMonth]);

  // Count stats
  const stats = useMemo(() => {
    if (!daysInMonth.length) return { holidays: 0, leaves: 0, weekends: 0, working: 0 };
    return {
      holidays: daysInMonth.filter((d) => d.is_holiday).length,
      leaves: daysInMonth.filter((d) => d.is_leave).length,
      weekends: daysInMonth.filter((d) => d.is_weekend).length,
      working: daysInMonth.filter((d) => d.is_working_day).length,
    };
  }, [daysInMonth]);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          Leave & Holiday Calendar
        </h1>
        <button
          onClick={goToday}
          className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
        >
          <CalendarDays className="h-4 w-4" /> Today
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
            <Briefcase className="h-4 w-4 text-green-600" />
          </div>
          <div>
            <div className="text-lg font-medium text-slate-950">{stats.working}</div>
            <div className="text-xs text-slate-500">Working Days</div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
            <Umbrella className="h-4 w-4 text-red-600" />
          </div>
          <div>
            <div className="text-lg font-medium text-slate-950">{stats.holidays}</div>
            <div className="text-xs text-slate-500">Holidays</div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
            <CalendarDays className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <div className="text-lg font-medium text-slate-950">{stats.leaves}</div>
            <div className="text-xs text-slate-500">Leave Days</div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
            <Moon className="h-4 w-4 text-slate-600" />
          </div>
          <div>
            <div className="text-lg font-medium text-slate-950">{stats.weekends}</div>
            <div className="text-xs text-slate-500">Weekends</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={goPrevMonth}
              className="p-2 hover:bg-slate-100 rounded-sm transition-colors text-slate-500 hover:text-slate-900"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h2 className="text-lg font-medium text-slate-900">
              {MONTHS[currentMonth - 1]} {currentYear}
            </h2>
            <button
              onClick={goNextMonth}
              className="p-2 hover:bg-slate-100 rounded-sm transition-colors text-slate-500 hover:text-slate-900"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 mb-2">
            {DAY_NAMES.map((name) => (
              <div key={name} className="text-center text-xs font-semibold uppercase tracking-wider text-slate-500 py-2">
                {name}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before the 1st */}
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="h-10 sm:h-14" />
            ))}

            {/* Day cells */}
            {daysInMonth.map((day) => {
              const dayNum = parseInt(day.date.split("-")[2], 10);
              const isToday = day.date === today.toISOString().split("T")[0];
              const isSelected = day.date === selectedDay;

              let cellClass = "h-10 sm:h-14 rounded-sm border border-transparent flex flex-col items-center justify-center cursor-pointer transition-all text-sm relative";

              if (day.is_saturday_working) {
                cellClass += " bg-amber-50 text-amber-700 border-amber-200";
              } else if (day.is_weekend && !day.is_holiday && !day.is_leave) {
                cellClass += " bg-slate-50 text-slate-400";
              } else if (day.is_holiday) {
                cellClass += " bg-red-50 text-red-700 border-red-200";
              } else if (day.is_leave) {
                cellClass += " bg-blue-50 text-blue-700 border-blue-200";
              } else {
                cellClass += " hover:bg-slate-50 text-slate-900";
              }


              if (isToday) {
                cellClass += " ring-2 ring-[#002FA7]";
              }
              if (isSelected) {
                cellClass += " ring-2 ring-amber-500";
              }

              return (
                <div
                  key={day.date}
                  className={cellClass}
                  onClick={() => setSelectedDay(day.date)}
                  title={
                    day.is_holiday
                      ? day.holiday_name
                      : day.is_leave
                      ? `${day.leave_type} (${day.leave_status})`
                      : day.date
                  }
                >
                  <span className={classNames(
                    "text-xs sm:text-sm font-medium",
                    isToday ? "text-[#002FA7]" : ""
                  )}>
                    {dayNum}
                  </span>
                  {/* Dot indicators */}
                  <div className="flex gap-0.5 mt-0.5">
                    {day.is_holiday && <div className="h-1 w-1 rounded-full bg-red-500" />}
                    {day.is_leave && <div className="h-1 w-1 rounded-full bg-blue-500" />}
                    {day.is_working_day && !day.is_weekend && (
                      <div className="h-1 w-1 rounded-full bg-green-500" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-6 pt-4 border-t border-slate-200">
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
              Working Day
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <div className="h-2.5 w-2.5 rounded-full bg-amber-500" />
              Saturday Working
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
              Public Holiday
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <div className="h-2.5 w-2.5 rounded-full bg-blue-500" />
              On Leave
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-600">
              <div className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              Weekend
            </div>
          </div>

        </div>

        {/* Day Details Panel */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">
            {selectedDayInfo ? "Day Details" : "Select a Day"}
          </h3>

          {selectedDayInfo ? (
            <div className="space-y-4">
              <div>
                <div className="text-lg font-medium text-slate-900">
                  {new Date(selectedDayInfo.date + "T12:00:00").toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>

              <div className="space-y-2">
                {/* Weekend indicator */}
                {selectedDayInfo.is_weekend && (
                  <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-sm px-3 py-2">
                    <Moon className="h-4 w-4 text-slate-400" />
                    <span>Weekend — Non-working day</span>
                  </div>
                )}

                {/* Holiday indicator */}
                {selectedDayInfo.is_holiday && (
                  <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 rounded-sm px-3 py-2">
                    <Umbrella className="h-4 w-4 text-red-500" />
                    <div>
                      <span className="font-medium">Public Holiday</span>
                      {selectedDayInfo.holiday_name && (
                        <span className="block text-xs text-red-600">{selectedDayInfo.holiday_name}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Leave indicator */}
                {selectedDayInfo.is_leave && (
                  <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 rounded-sm px-3 py-2">
                    <CalendarDays className="h-4 w-4 text-blue-500" />
                    <div>
                      <span className="font-medium">On Leave</span>
                      {selectedDayInfo.leave_type && (
                        <span className="block text-xs text-blue-600">
                          {selectedDayInfo.leave_type}
                          {selectedDayInfo.leave_status && ` (${selectedDayInfo.leave_status})`}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Saturday working day indicator */}
                {selectedDayInfo.is_saturday_working && (
                  <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-sm px-3 py-2">
                    <Briefcase className="h-4 w-4 text-amber-500" />
                    <div>
                      <span className="font-medium">Saturday Working Day</span>
                      {selectedDayInfo.saturday_working_type && (
                        <span className="block text-xs text-amber-600 capitalize">
                          {selectedDayInfo.saturday_working_type === "half_day" ? "Half Day" : "Full Day"}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Working day indicator */}
                {selectedDayInfo.is_working_day && !selectedDayInfo.is_weekend && !selectedDayInfo.is_saturday_working && (
                  <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-sm px-3 py-2">
                    <Briefcase className="h-4 w-4 text-green-500" />
                    <span>Working Day</span>
                  </div>
                )}

              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarDays className="h-8 w-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Click on a day to see details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
